import { message, notification } from 'antd'
import { createContext, useContext } from 'react'
import { useSpreadSheetData } from './SpreadSheetDataProvider'
import { useHotKeys } from 'Hooks'
import { clearEdits, performRedo, performUndo, setNodeOrder, setRelationsMap, setEdits } from './SpreadSheetDataProvider/actions'
import { useTableData } from './TableDataProvider'
import { getMemberKey, getUid } from 'Services/WeaverUtils'
import { clearTableData,  setTableData } from './TableDataProvider/actions'
import WeaverService from 'Services/WeaverService'
import {
  fetchRelations,
  filterReadOnly,
  getBoundingBox,
  getCellValues,
  getPasteMatrix,
  getPasteOffset,
  getSelectionMatrix
} from './helpers'

EditsHelpersContext = createContext()

EditsHelpersProvider = ({ children, onRemoveNodes, subject }) ->

  [{
    columns,
    count,
    editHistory,
    edits,
    nodeOrder,
    relationsMap,
    rowsToBeDeleted,
    selection,
    selectionType,
    undoHistory
  }, dispatch] = useSpreadSheetData()

  [tableData, dispatchTableData] = useTableData()

  useHotKeys([{
    sequence: ['mod+z']
    fn: ->
      if editHistory.length > 0
        dispatch(performUndo(edits))
    temporary: true
    watchers: [edits, editHistory]
  }, {
    sequence: ['mod+shift+z']
    fn: ->
      if undoHistory.length > 0
        dispatch(performRedo(edits))
    temporary: true
    watchers: [edits, undoHistory]
  }, {
    sequence: ['mod+c']
    fn: (e) ->
      [x1, y1, x2, y2] = getBoundingBox(selection, selectionType)

      relevantNodes = nodeOrder.slice(y1, y2)
        .map((id) -> tableData[id])
      relevantColumns = columns.slice(x1, x2)

      relevantEdits = [y1..y2].map((i) ->
        [x1..x2].map((j) ->
          edits[i]?[j]
        )
      )

      { cellData, foundEmptyRow } = getCellValues(
        relevantNodes,
        relevantColumns,
        relevantEdits
      )

      snippet = cellData
        .map((row) -> row.join('\t'))
        .join('\n')

      if foundEmptyRow
        message.info('You tried to copy data from rows which have not yet been loaded.')

      navigator.clipboard.writeText(snippet)

    temporary: true
    watchers: [edits, selection, selectionType]
  }, {
    sequence: ['mod+v']
    fn: (e) ->
      clipboardData = await navigator.clipboard.readText()
      fromData = clipboardData
        .split('\n')
        .map((row) -> row.split('\t'))
      isRepeatingVal = fromData.length is 1 and fromData[0].length is 1

      [x, y] = getPasteOffset(selection, selectionType)
      toData = getSelectionMatrix(selection, selectionType, count, columns.length)

      { formattedData, relationTargets } = getPasteMatrix(fromData, toData, [x, y], isRepeatingVal, columns)

      customizer = (objVal, srcVal) ->
        if _.isArray(srcVal)
          return srcVal

      newRelations = await fetchRelations(relationTargets)
      dispatch(
        setRelationsMap({ relationsMap..., newRelations... })
      )

      nextEdits = _.mergeWith({ edits... }, filterReadOnly(formattedData, columns), customizer)
      dispatch(setEdits({
        prevEdits: { edits... }
        nextEdits
      }))
    expr: WeaverService.userCanEdit()
    temporary: true
    watchers: [edits, selection, selectionType]
  }])

  loadApplicableNodes = ->
    dirtyRowIndices = _.keys(edits).filter((i) -> edits[i])
    idsOfUnloadedNodes = dirtyRowIndices.map((i) ->
      nodeOrder[i]
    ).filter((id) -> !tableData[id])

    loadedMap = (await new Weaver.Query()
      .restrict(idsOfUnloadedNodes)
      .unlimited()
    ).reduce((acc, curr) ->
      { acc..., [getUid(curr)]: curr }
    , {})

    { loadedTableData: { tableData..., loadedMap... } }

  applyDatasetRules = (n) ->
    if subject isnt 'Untyped'
      n.relation(getMemberKey())
        .add(Weaver.Node.getFromGraph(subject.split('@')...))

  newNode = (colIndex, newValue) ->
    if columns[colIndex].createWithId?
      new Weaver.Node(newValue)
    else
      new Weaver.Node()

  removeDuplicateIds = (tableData) ->
    uniqData = _.uniqBy(tableData, 'nodeId')
    ids = tableData.filter((n) -> n?).map((n) -> getUid(n))
    duplicateIds = _.uniq(ids.filter((item, i) -> ids.indexOf(item) isnt i))

    if duplicateIds.length > 0
      notification.error({
        message: "Duplicate node identifiers found"
        description: <div>
          An attempt was made to save multiple nodes with identical identifiers.<br/>
          As a result only one instance of each node with the following identifiers were saved.<br/><br/>
          <em>{ duplicateIds.toString().replace(/[,]+/g, ", ") }</em>
        </div>
        duration: 15
      })

    uniqData

  handleErrMessage = (savingMessage, err) ->
    savingMessage()
    splitMessage = err.message.split("ERROR: ")
    if splitMessage[1].includes("duplicate key value violates unique constraint")
      message.error("There was an error saving. Duplicate node identifiers were found in the space.")
    else
      message.error("There was an error saving, see the console for details.")
      console.error(err)

  saveChanges = ->
    editCount = 0
    { loadedTableData } = await loadApplicableNodes()
    newTableData = nodeOrder.map((id) -> loadedTableData[id])
    newNodeOrder = [nodeOrder...]

    # Apply edits to nodes (including newly created nodes).
    _.map(edits, (row, i) ->
      _.map(row, (cell, j) ->
        return unless columns[j].editFn? or columns[j].createWithId?
        editCount++
        newVal = edits[i][j]
        if columns[j].relationConf then newVal = edits[i][j].map((id) -> relationsMap[id])
        if not newTableData[i]
          n = newNode(j, newVal)
          applyDatasetRules(n)
          newTableData[i] = n
          newNodeOrder[i] = n.id()
        columns[j].editFn && columns[j].editFn(newTableData[i], newVal)
      ).filter((cell) -> cell?)
    ).filter((row) -> row? )
    .flat()

    newTableData = removeDuplicateIds(newTableData)

    # Delete any nodes that are scheduled for deletion.
    toBeDeleted = []

    newTableData.map((n, i) ->
      if rowsToBeDeleted[i]
        editCount++
        toBeDeleted.push(getUid(newTableData[i]))
        newTableData[i].destroySync()
    )
    onRemoveNodes(toBeDeleted)

    _.remove(newTableData, (el) -> !el?)
    _.remove(newNodeOrder, (id, i) -> rowsToBeDeleted[i])

    if editCount > 0
      try
        savingMessage = message.loading("Dispatching #{ editCount } changes", 0)
        await Weaver.Node.batchSave(newTableData, { commitMessage: "Make #{ editCount } edits in the #{ subject } table." })
        savingMessage()
        message.success("Saved #{ editCount } change#{ if editCount > 1 then 's' else '' }.")

        dispatch(setNodeOrder(newNodeOrder))
        dispatchTableData(clearTableData())
        dispatchTableData(
          setTableData(
            newTableData.reduce((acc, id) ->
              { acc..., [id]: undefined }
            , {})
          )
        )
      catch err
        handleErrMessage(savingMessage, err)

    discardChanges()

  discardChanges = ->
    dispatch(clearEdits())

  <EditsHelpersContext.Provider
    value={{
      discardChanges,
      saveChanges
    }}
  >
    { children }
  </EditsHelpersContext.Provider>

export useEditsHelpers = () ->
  useContext(EditsHelpersContext)

export { EditsHelpersProvider }
